/**
 * Which days may the wastage date picker offer?
 *
 * The twin of utils/stockInDateBounds.js, and it repeats that file's central lesson:
 * a wrongly disabled day has NO onClick, so tapping it does nothing, the popup stays
 * open, and the field goes on showing the old date. People then submit believing they
 * back-dated the wastage, and it lands on today in every report that filters
 * StockOutRecords.deducted_date.
 *
 * Stock-in's rule got this wrong by flooring the calendar at `onboardingDate || today`,
 * where onboardingDate is fetched asynchronously — so before the fetch resolved, every
 * past day was dead. The lesson is encoded here as a rule:
 *
 *   • The CEILING is always known — you cannot waste stock in the future — so it is
 *     always enforced.
 *   • The FLOOR comes from the server (`reconciled_through`, how far back closing stock
 *     has certified the ledger). Until it arrives it is null, and null means NO LOWER
 *     BOUND, never "today". We do not invent a date to clamp against.
 *
 * The one deliberate consequence: for the moment between the screen opening and
 * /wastage/date-bounds answering, a reconciled day is selectable. That is fine — the
 * server refuses it at submit with PERIOD_RECONCILED, and an occasional rejected submit
 * is a far smaller failure than a calendar that silently cannot back-date at all.
 */

/** Midnight of a date, in local time, without mutating the input. */
function atMidnight(value) {
  if (value === null || value === undefined) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
}

/** A Date as its LOCAL calendar day, 'YYYY-MM-DD'. */
function toYmd(date) {
  const d = atMidnight(date);
  if (!d) return null;
  // toLocaleDateString('en-CA') is ISO-shaped and, unlike toISOString(), local.
  return d.toLocaleDateString('en-CA');
}

/**
 * Parse 'YYYY-MM-DD' (or a full ISO timestamp) as LOCAL midnight.
 *
 * new Date('2026-08-15') is UTC midnight. In IST that is still the 15th, but in any
 * negative-offset environment it is the 14th — enough to open the calendar on the wrong
 * month, which is exactly the bug openProductionPicker() works around in StockInForm.
 */
function parseYmd(value) {
  if (!value || typeof value !== 'string') return null;
  const parts = value.split('T')[0].split('-');
  if (parts.length !== 3) return null;
  const [y, m, d] = parts.map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  const out = new Date(y, m - 1, d);
  return Number.isNaN(out.getTime()) ? null : out;
}

/**
 * An optional caller-supplied bound ('YYYY-MM-DD' or a Date) as local midnight.
 * Unreadable means ABSENT, for the same reason a malformed watermark does: a
 * clamp invented from a value we could not read is how every day goes dead.
 */
function readBound(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string') return atMidnight(parseYmd(value));
  if (value instanceof Date) return atMidnight(value);
  return null;
}

/**
 * @param {Date} cellDate the calendar day being rendered
 * @param {{reconciledThrough?: string|null, now?: Date,
 *          minDate?: string|Date|null, maxDate?: string|Date|null}} [options]
 *   `minDate` / `maxDate` narrow the range further (e.g. a banquet's wastage
 *   window). They can only ever NARROW: today stays the ceiling and the
 *   watermark stays the floor whatever they say. Absent, they change nothing.
 * @returns {boolean} true when the day must not be selectable
 */
function isWastageDateDisabled(cellDate, options = {}) {
  const { reconciledThrough = null, now = new Date(), minDate = null, maxDate = null } = options;

  const cell = atMidnight(cellDate);
  const today = atMidnight(now);
  if (!cell || !today) return true; // an unreadable day is never safe to offer

  if (cell > today) return true; // no wasting stock in the future

  const upper = readBound(maxDate);
  if (upper && cell > upper) return true;

  const lower = readBound(minDate);
  if (lower && cell < lower) return true;

  if (reconciledThrough) {
    const floor = atMidnight(parseYmd(reconciledThrough));
    // A malformed watermark is treated as absent rather than as a clamp: inventing a
    // floor from a value we could not read is how every past day goes dead.
    if (floor && cell <= floor) return true;
  }

  return false;
}

export { isWastageDateDisabled, atMidnight, toYmd, parseYmd };
